import { Ticket } from "@assets/icons";
import { SelectPathEntity } from "@common/components/InstanceSelector";
import { RenderIconSorting } from "@common/components/ListDetailViews/ListView/ListCell/header";
import Typography from "@common/components/Typography";
import {
  getCurrentSizeProfile,
  responsiveFontSize,
} from "@common/components/Typography/style";
import { WithGrow } from "@common/components/WithGrow";
import { objectToSearchParams } from "@common/utils/filters/mappers/common";
import {
  canViewAccount,
  canViewDevice,
  canViewLocation,
  canViewRoom,
} from "@common/utils/user";
import { Divider, Stack, Tooltip } from "@mui/material";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import React from "react";
import { TFunction } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

export enum DrawerClosingReason {
  CANCELED,
  SAVED,
  DELETED,
  CLOSE_WITHOUT_SAVING,
}

type AssetNavigationParams = {
  fromAccount?: string;
  fromCountry?: string;
  fromLocation?: string;
  fromRoom?: string;
  watchList?: string;
};
export const assetNavigationKeys: Required<{
  [k in keyof AssetNavigationParams]: k;
}> = {
  fromAccount: "fromAccount",
  fromCountry: "fromCountry",
  fromLocation: "fromLocation",
  fromRoom: "fromRoom",
  watchList: "watchList",
};
export const listViewInstancesTitles = {
  Accounts: "Accounts",
  Countries: "Countries",
  Locations: "Locations",
  Rooms: "Rooms",
  Devices: "Devices",
};
export const listViewInstances: (SelectPathEntity & {
  accessFn: typeof canViewAccount;
  navigationKey?: keyof AssetNavigationParams;
})[] = [
  {
    title: listViewInstancesTitles.Accounts,
    path: "/assets/accounts",
    accessFn: canViewAccount,
    navigationKey: assetNavigationKeys.fromAccount,
  },
  {
    title: listViewInstancesTitles.Countries,
    path: "/assets/countries",
    accessFn: canViewAccount,
    navigationKey: assetNavigationKeys.fromCountry,
  },
  {
    title: listViewInstancesTitles.Locations,
    path: "/assets/locations",
    accessFn: canViewLocation,
    navigationKey: assetNavigationKeys.fromLocation,
  },
  {
    title: listViewInstancesTitles.Rooms,
    path: "/assets/rooms",
    accessFn: canViewRoom,
    navigationKey: assetNavigationKeys.fromRoom,
  },
  {
    title: listViewInstancesTitles.Devices,
    path: "/assets/devices",
    accessFn: canViewDevice,
  },
];

export const useInstanceSelectorProps = (): {
  name: string;
  instances: SelectPathEntity[];
  instance: SelectPathEntity;
  onChange: (path: SelectPathEntity) => void;
} => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user] = useAuthenticatedUser();
  const name = location.pathname
    .split("/")
    .filter((n) => n)
    .at(-1) as string;
  const instance = listViewInstances.find((i) =>
    i.path?.toLocaleLowerCase().includes(name?.toLocaleLowerCase())
  ) ?? {
    title: "",
    path: "/",
  };
  return {
    name,
    instances: listViewInstances
      .filter(({ title }) => title !== listViewInstancesTitles.Countries)
      .filter(({ accessFn }) => accessFn(user)),
    instance,
    onChange: ({ path }: SelectPathEntity) => navigate(path, { state: {} }),
  };
};

export type AssetName = keyof typeof listViewInstancesTitles;
const getUrl =
  (navigationParams: AssetNavigationParams) =>
  (from: { [k in AssetName]?: string }, to: AssetName) => {
    const baseUrl = listViewInstances.find(({ title }) => title === to)?.path;

    if (baseUrl === undefined) return;

    let key: keyof AssetNavigationParams | undefined = undefined;
    (Object.entries(from) as Array<[AssetName, string]>).forEach(
      ([from, id]) => {
        switch (from) {
          case "Accounts":
            key = assetNavigationKeys.fromAccount;
            break;
          case "Countries":
            key = assetNavigationKeys.fromCountry;
            break;
          case "Locations":
            key = assetNavigationKeys.fromLocation;
            break;
          case "Rooms":
            key = assetNavigationKeys.fromRoom;
            break;
        }
        if (key !== undefined) navigationParams[key] = id;
      }
    );

    return `${baseUrl}?${objectToSearchParams(navigationParams)}`;
  };

export const useAssetNavigationParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const navigationParams: AssetNavigationParams = {};
  (Object.keys(assetNavigationKeys) as (keyof AssetNavigationParams)[]).map(
    (k) => (navigationParams[k] = searchParams.get(k) ?? undefined)
  );

  return {
    navigationParams,
    getTargetUrl: getUrl(navigationParams),
    setSearchParams,
  };
};

export const getAssetRenderHeaderFn =
  <T, O>(
    t: TFunction,
    customKey: keyof T,
    hasBookmark: boolean = false
  ): Required<ColDef<T, O>>["renderHeader"] =>
  (column, sorting) => {
    const onSort = sorting.onSort ?? (() => {});
    const sort = sorting.sort;
    return (
      <WithGrow
        direction="row"
        fixed={
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              cursor: "pointer",
              "&:hover *": {
                visibility: "visible",
              },
            }}
            onClick={() =>
              onSort(
                customKey,
                customKey === sort?.column && sort?.direction === "asc"
                  ? "desc"
                  : "asc"
              )
            }
          >
            <Tooltip title={t("Open tickets")}>
              <span style={{ display: "flex" }}>
                <Ticket />
              </span>
            </Tooltip>
            <RenderIconSorting
              col={{ ...column }}
              customKey={customKey}
              {...sorting}
            />
            <Divider flexItem orientation="vertical" sx={{ ml: 1 }} />
          </Stack>
        }
        sx={
          hasBookmark
            ? (theme) => ({
                // Should be roughly on top of the tickets, but no way we're centering it exactly, just way too much fiddling
                fontSize: getCurrentSizeProfile().small,
                ...responsiveFontSize(theme, "small"),
                paddingLeft: `calc( 2em + ${theme.spacing(5)} )`,
              })
            : { paddingLeft: 1 }
        }
        containerSx={{
          pl: 2,
          alignItems: "center",
          display: "flex",
        }}
        fixedSx={{
          alignItems: "center",
          display: "flex",
        }}
      >
        <Stack
          direction="row"
          onClick={() =>
            onSort(
              column.key,
              column.key === sort?.column && sort?.direction === "asc"
                ? "desc"
                : "asc"
            )
          }
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            alignContent: "center",
            cursor: "pointer",

            "&:hover *": {
              visibility: "visible",
            },
          }}
        >
          <Typography
            size="small"
            sx={{
              alignSelf: "center",
              textAlign: "center",
            }}
          >
            {t(column.label)}
          </Typography>
          <RenderIconSorting col={{ ...column }} {...sorting} />
        </Stack>
      </WithGrow>
    );
  };

export const provisioningListViewSx = {
  "& th:nth-of-type(2)": {
    paddingLeft: 1,
  },
  "& th:nth-of-type(n+3)": {
    paddingLeft: 0,
  },
  "& th:last-child": {
    paddingRight: 1,
  },
};
